import { Trans, t } from '@lingui/macro'
import { observer, useLocalStore } from 'mobx-react-lite'
import Link from 'next/link'
import React from 'react'
import { useRouter } from 'next/router'
import {
  Bar,
  BarItem,
  Button,
  ButtonLayout,
  ButtonPrimary,
  Card,
  CardContent,
  CardFooter,
  Container,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Grid,
  GridCol,
  Icon,
  Notice,
  Overlay,
  ReachDialog,
  Tooltip,
} from '../../ui-kit'
import { PRODUCT_TYPE, URLS } from '../../../config/constants'
import { _, i18n } from '../../utils/intl'

import { ApiOrdersRepository } from '../../api/ApiOrdersRepository'
import { ModalAuth } from '../../../client/order/ModalAuth'
import { ORDER_ID_LOCALSTORAGE_KEY } from '../../../client/order/StoreOrder'
import { ReachConfirmDialog } from '../modals/ConfirmModal'
import { appStore } from '../../AppStore'
import { clientStore } from '../../../client/ClientStore'
import { ordersApi } from '../../api'
import { storeMergedProducts } from '../../StoreProducts'
import { useDialog } from '../../utils/hooks/useDialog'
import { useDidMount } from '../../utils/hooks/useDidMount'

export const PageProductSelection: React.FC<{ isAuthorized: boolean }> = observer(
  function PageProductSelection({ isAuthorized }) {
    const isMigratedUser = appStore.isMigratedUser

    const d = useDialog()
    const signInDialog = useDialog()
    const dialogPreventAnotherProduct = useDialog()
    const r = useRouter()

    const state = useLocalStore(() => ({
      order: undefined as string | undefined,
      async fetchOrder() {
        if (isAuthorized) {
          const clientId = clientStore.client?.id ?? '0'
          try {
            this.order = (await ordersApi.getOrderInProgress(clientId)).data
          } catch (err) {}
          if (this.order) {
            d.open()
          }
        } else {
          const orderId = localStorage.getItem(`0.${ORDER_ID_LOCALSTORAGE_KEY}`)

          if (orderId) {
            await ApiOrdersRepository.deleteOrder(orderId)
            localStorage.removeItem(`0.${ORDER_ID_LOCALSTORAGE_KEY}`)
          }
        }
      },
      async onNewOrder() {
        if (this.order) await ApiOrdersRepository.deleteOrder(this.order)
        d.close()
      },
      defaultAuthModalTabIndex: 0 as 0 | 1,
      openSignInDialog(idx: 0 | 1) {
        this.defaultAuthModalTabIndex = idx
        signInDialog.open()
        dialogPreventAnotherProduct.close()
      },
    }))

    useDidMount(() => {
      state.fetchOrder()
    })

    return (
      <Overlay bgSrc="/static/img/overlay-background.jpg" color="transparent">
        <Container>
          {!isAuthorized && (
            <Notice id="are-you-customer" icon="bulb" closable className="mb-xxlarge">
              <Link href={URLS.auth('/client/product-selection')}>
                <a className="link-black">
                  <Trans>Ste už zákazníkom? Prihláste sa a objednávací proces bude rýchlejší</Trans>
                </a>
              </Link>
            </Notice>
          )}
          <h1 className="text-color-white mb-xxlarge">
            <Trans>Výber produktu</Trans>
          </h1>
          <div style={{ minHeight: 600 }}>
            <Grid>
              <GridCol
                size={{
                  xl: 9,
                }}>
                <Grid className="product-grid">
                  {storeMergedProducts.apiProducts.map(product => (
                    <GridCol size={{ m: 6, l: 4 }} key={product.id} equalizeHeight>
                      {/* FIXME: ? */}
                      <Card
                        href={`/client/order?product=${product.id}`}
                        onClick={e => {
                          if (!isAuthorized && product.product_type_id === PRODUCT_TYPE.Card) {
                            e.preventDefault()
                            dialogPreventAnotherProduct.open()
                          }
                        }}>
                        <div className="card__bg">
                          <div
                            className="flex align-items-center"
                            style={{ width: '6.5rem', height: '6.5rem' }}>
                            <img src={product.icon['1x']} width="59" alt={product.title} />
                          </div>
                        </div>
                        <CardContent fill>
                          <Bar align="top" className="mb-none">
                            <BarItem isFilling>
                              <h2 className="h4">
                                <Link href={URLS.order({ product: String(product.id) })}>
                                  <a
                                    style={{ wordSpacing: '100vw' }}
                                    // onClick={async e => {
                                    //   e.preventDefault()
                                    //   if (state.order) {
                                    //   }
                                    // }}
                                  >
                                    {product.title}
                                  </a>
                                </Link>
                              </h2>
                            </BarItem>
                            <BarItem>
                              <Icon name="arrow-right" className="h3 text-color-theme-500" />
                            </BarItem>
                          </Bar>
                        </CardContent>
                        <CardFooter clean className="show-m">
                          <Tooltip
                            className="block-link__subitem"
                            style={{ display: 'inline-block' }}
                            /* content={
                              <Link href={URLS.productDetail({ slug: product.slug! })}>
                                <a className="link-black">
                                  <Trans>Viac info</Trans>
                                </a>
                              </Link>
                            } */
                            dialog={_(t`Zobraziť produktovú stránku.`)}
                          />
                        </CardFooter>
                      </Card>
                    </GridCol>
                  ))}
                </Grid>
              </GridCol>
            </Grid>
          </div>
        </Container>
        {isMigratedUser
          ? null
          : d.isOpen && (
              <ReachDialog size="xlarge">
                <DialogHeader title={i18n.t`Upozornenie`} hideCloseBtn />
                <DialogContent>
                  <Grid>
                    <GridCol>
                      <Trans>
                        Chcete pokračovat v poslednej rozpracovanej objednávke alebo vytvoriť novú?
                      </Trans>
                    </GridCol>
                  </Grid>
                </DialogContent>
                <DialogFooter>
                  <ButtonLayout direction="horizontal" className="align-items-center">
                    <Button onClick={state.onNewOrder} data-testid="btn-cancel">
                      <Trans>Nová objednávka</Trans>
                    </Button>

                    <ButtonPrimary
                      onClick={() => {
                        r.push(URLS.order())
                        d.close()
                      }}
                      // isDisabled={disabledConfirmButton}
                      data-testid="btn-confirm">
                      <Trans>Pokračovať v objednávke</Trans>
                    </ButtonPrimary>
                  </ButtonLayout>
                </DialogFooter>
              </ReachDialog>
            )}

        {signInDialog.isOpen && (
          <ModalAuth
            close={signInDialog.close}
            showProductRequiresAuthMessage
            defaultActiveTabIndex={state.defaultAuthModalTabIndex}
          />
        )}

        {dialogPreventAnotherProduct.isOpen && (
          <ReachConfirmDialog
            close={dialogPreventAnotherProduct.close}
            buttonCancelText={i18n.t`Zatvoriť`}
            buttonConfirmText={i18n.t`Prihlásiť sa`}
            message={
              <>
                <Trans>
                  Pre nákup tohto produktu musíte byť prihlásený. Ak u nás ešte nemáte účet, môžete
                  sa{' '}
                  <a className="link-black" onClick={() => state.openSignInDialog(1)}>
                    zaregistrovať
                  </a>
                </Trans>
                .
              </>
            }
            buttonConfirmAction={() => state.openSignInDialog(0)}
          />
        )}
      </Overlay>
    )
  }
)
